(() => {
  const html = document.documentElement;
  const isDesignMode = () =>
    Boolean(window.Shopify?.designMode) || html.classList.contains('shopify-design-mode');

  const bailForEditor = () => {
    html.classList.remove('kb-motion-boot', 'is-intro-pending');
    html.classList.add('kb-skip-intro', 'is-intro-done');
    document.querySelector('[data-motion-intro]')?.remove();
  };

  if (isDesignMode()) {
    bailForEditor();
    return;
  }

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    bailForEditor();
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  const DESKTOP_MQ = '(min-width: 990px)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia(DESKTOP_MQ);
  let REDUCED = reducedMotion.matches;
  const EASE_SOFT = 'power3.out';
  const EASE_VEIL = 'power4.inOut';
  const SLIDE_Y = 40;

  const isCarouselSnapChild = (el) =>
    el instanceof Element && el.matches('slideshow-slides > .card, .card[data-carousel-card]');

  const fromVars = (el) =>
    isCarouselSnapChild(el)
      ? { opacity: 0 }
      : { opacity: 0, y: SLIDE_Y, force3D: true };

  const toVars = (el) =>
    isCarouselSnapChild(el)
      ? { opacity: 1, clearProps: 'transform' }
      : { opacity: 1, y: 0, force3D: true };
  const SCRUB = 0.4;
  const SCROLL_START = 'top 92%';
  const SCROLL_END = 'top 62%';

  const pageWrapper = () => document.querySelector('.page-wrapper');
  const isDesktop = () => desktop.matches;
  const scroller = () => (isDesktop() ? pageWrapper() || window : window);

  const targetsFor = (el) => {
    if (!el.hasAttribute('data-reveal-stagger')) return [el];
    return [...el.children].filter((child) => !child.hasAttribute('data-reveal-skip'));
  };

  const uniqueOutermost = (els) =>
    els.filter((el) => !els.some((other) => other !== el && other.contains(el)));

  const revealGroupsFor = (el) => {
    if (el.closest('[id*="why_kleer"], [data-section-id*="why_kleer"]')) {
      const topLevel = [...el.children].filter((child) => child.matches('.group-block'));
      const intro = topLevel[0] ? [topLevel[0]] : [];
      const cardRow = topLevel[1]?.querySelector(':scope > .group-block-content');
      const cards = cardRow
        ? [...cardRow.children].filter((child) => child.matches('.group-block'))
        : [];

      if (cards.length) {
        return [{ items: intro }, { items: cards }].filter((group) => group.items.length);
      }
    }

    if (el.matches('.testing-made-simple__steps')) {
      return [{ items: [...el.querySelectorAll(':scope > .testing-made-simple__step')] }];
    }

    if (el.matches('.badge-row')) {
      const badges = [
        ...el.querySelectorAll('.badge-row__group:not(.badge-row__group--clone) .badge-row__item'),
      ];
      if (badges.length) return [{ items: badges }];
    }

    const cards = uniqueOutermost([
      ...el.querySelectorAll(
        'slideshow-slides > .card, .resource-list__item, .card-product, .product-card, .resource-card'
      ),
    ]);
    if (cards.length) return [{ items: cards }];

    const items = targetsFor(el);

    const groupCardRow = [...el.querySelectorAll('.group-block-content.layout-panel-flex--row')].find(
      (row) => {
        const children = [...row.children].filter((child) => child.matches('.group-block'));
        return children.length > 1 && children.some((child) => child.querySelector('img, picture'));
      }
    );
    if (groupCardRow) {
      const groupCards = [...groupCardRow.children].filter((child) =>
        child.matches('.group-block')
      );
      const intro = items.filter((item) => !item.contains(groupCardRow));
      return [{ items: intro }, { items: groupCards }].filter((group) => group.items.length);
    }

    return [{ items }];
  };

  const isIntroReveal = (el) => el.matches('.hero__content-wrapper[data-reveal]');

  const revealTriggerFor = (el) => {
    if (
      el.matches(
        [
          '.water-questions__viewport',
          '.water-questions__footer',
          '.kit-comparison__boards',
          '.early-reviews__list',
          '.early-reviews__footnote',
          '.personalized-report__features',
          '.personalized-report__media',
          '.kit-details__panel',
          '.feature-items__list',
          '.testimonial-slider',
        ].join(', ')
      )
    ) {
      return el.closest('.shopify-section') || el;
    }
    return el;
  };

  const scrubST = (trigger, start = SCROLL_START, end = SCROLL_END) => ({
    trigger,
    scroller: scroller(),
    start,
    end,
    scrub: SCRUB,
    invalidateOnRefresh: true,
  });

  const ensureSectionReveals = () => {
    document
      .querySelectorAll('#MainContent > .shopify-section, #footer-group > .shopify-section')
      .forEach((section) => {
        if (section.querySelector('.faqs')) return;
        if (
          section.querySelector(
            '[data-reveal], [data-reveal-footer], [data-reveal-footer-utilities], [data-kb-no-reveal]'
          )
        ) {
          return;
        }

        const target =
          section.querySelector('.badge-row__viewport') ||
          section.querySelector('.section-content-wrapper') ||
          section.querySelector('.section') ||
          section;

        target.setAttribute('data-reveal', 'up');

        const busy = target.querySelector(
          'marquee-component, .badge-row__track, .slider, slideshow-component'
        );
        if (busy) return;

        const kids = [...target.children].filter(
          (child) => child.nodeType === 1 && !child.classList.contains('section-background')
        );
        if (kids.length > 1 && kids.length <= 10) {
          target.setAttribute('data-reveal-stagger', '');
        }
      });
  };

  const footerMotionGroups = () => {
    const primary = document.querySelector('[data-reveal-footer]');
    const utilities = primary?.querySelector('[data-reveal-footer-utilities]');
    const find = (selector) => (primary ? [...primary.querySelectorAll(selector)] : []);

    return {
      primary,
      logo: find('[data-footer-logo]'),
      columns: find('[data-footer-column]'),
      newsletter: find('[data-footer-newsletter]'),
      utilities: utilities ? [...utilities.children] : [],
    };
  };

  const primeFirstPaint = () => {
    const heroContent = document.querySelector('.hero__content-wrapper[data-reveal]');
    const heroKids = heroContent ? targetsFor(heroContent) : [];
    if (heroKids.length) gsap.set(heroKids, fromVars());
  };

  const buildFirstPaint = (tl) => {
    const mobile = !isDesktop();
    const heroContent = document.querySelector('.hero__content-wrapper[data-reveal]');
    const heroKids = heroContent ? targetsFor(heroContent) : [];

    primeFirstPaint();

    if (heroKids.length) {
      tl.to(
        heroKids,
        {
          ...toVars(),
          duration: mobile ? 0.55 : 0.7,
          stagger: 0.08,
          ease: EASE_SOFT,
          clearProps: 'transform',
        },
        0
      );
    }

    return tl;
  };

  const prepare = (primeIntro = true) => {
    ensureSectionReveals();

    const els = [...document.querySelectorAll('[data-reveal]')];
    for (const el of els) {
      if (isIntroReveal(el)) continue;
      if (el.closest('.faqs')) continue;
      if (el.matches('.complex-results__media')) {
        // Keep the frame visible; animate image + bubbles separately.
        gsap.set(el, { opacity: 1, y: 0, clearProps: 'transform' });
        const image = el.querySelector('.complex-results__image');
        if (image) gsap.set(image, fromVars());
        continue;
      }
      revealGroupsFor(el).forEach((group) =>
        group.items.forEach((item) => gsap.set(item, fromVars(item)))
      );
    }

    const footer = footerMotionGroups();
    gsap.set([...footer.logo, ...footer.columns, ...footer.newsletter, ...footer.utilities], {
      opacity: 0,
    });

    gsap.set(document.querySelectorAll('.complex-results__hotspot'), fromVars());

    if (primeIntro) primeFirstPaint();
    return els;
  };

  const showImmediate = () => {
    gsap.set(document.querySelectorAll('[data-reveal], [data-reveal] > *'), {
      clearProps: 'transform,opacity,visibility',
    });
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      revealGroupsFor(el).forEach((group) =>
        gsap.set(group.items, { clearProps: 'transform,opacity,visibility' })
      );
    });
    const footer = footerMotionGroups();
    gsap.set([...footer.logo, ...footer.columns, ...footer.newsletter, ...footer.utilities], {
      clearProps: 'transform,opacity,visibility',
    });
    gsap.set(document.querySelectorAll('.complex-results__hotspot'), {
      clearProps: 'transform,opacity,visibility',
    });
    gsap.set(document.querySelectorAll('.hero__media-grid'), {
      clearProps: 'transform,opacity,visibility',
    });
  };

  const addRevealGroup = (tl, group, groupIndex) => {
    group.items.forEach((item, i) => {
      tl.fromTo(
        item,
        fromVars(item),
        { ...toVars(item), duration: 0.7, ease: 'none' },
        groupIndex * 0.08 + i * 0.1
      );
    });
  };

  const bindReveal = (el) => {
    if (el.matches('.complex-results__media')) {
      const image = el.querySelector('.complex-results__image');
      const hotspots = [...el.querySelectorAll('.complex-results__hotspot')];
      const tl = gsap.timeline({
        scrollTrigger: scrubST(el, 'top 90%', 'top 25%'),
      });

      gsap.set(el, { opacity: 1, y: 0 });
      if (image) {
        gsap.set(image, fromVars());
        tl.fromTo(image, fromVars(), { ...toVars(), ease: 'none' }, 0);
      }

      hotspots.forEach((spot, i) => {
        gsap.set(spot, fromVars());
        tl.to(spot, { ...toVars(), ease: 'none' }, 0.2 + i * 0.12);
      });
      return;
    }

    const scrollTrigger = el.matches('.testing-made-simple__steps')
      ? scrubST(el.closest('.shopify-section') || el, 'top 55%', 'top 30%')
      : scrubST(revealTriggerFor(el));
    const tl = gsap.timeline({ scrollTrigger });
    revealGroupsFor(el).forEach((group, i) => addRevealGroup(tl, group, i));
  };

  const bindFooter = () => {
    const footer = footerMotionGroups();
    if (!footer.primary) return;

    const trigger = footer.primary.closest('#footer-group') || footer.primary;
    const items = [...footer.logo, ...footer.columns, ...footer.newsletter, ...footer.utilities];
    const tl = gsap.timeline({
      scrollTrigger: {
        ...scrubST(trigger, 'top 95%', 'top 55%'),
        onRefresh(self) {
          if (self.progress > 0.01) gsap.set(items, { opacity: 1 });
        },
      },
    });
    const reveal = (group, position, stagger = 0.1) => {
      if (!group.length) return;
      tl.to(group, { opacity: 1, stagger, ease: 'none' }, position);
    };

    reveal(footer.logo, 0);
    reveal(footer.columns, 0.12, 0.14);
    reveal(footer.newsletter, 0.3, 0.12);
    reveal(footer.utilities, 0.52, 0.1);
  };

  const bindMediaParallax = () => {
    const medias = document.querySelectorAll('.hero__media-grid');

    medias.forEach((media) => {
      const section = media.closest('.shopify-section, .hero') || media;
      gsap.fromTo(
        media,
        { y: 0 },
        {
          y: 80,
          ease: 'none',
          force3D: true,
          scrollTrigger: scrubST(section, 'top top', 'bottom top'),
        }
      );
    });
  };

  let scrollCtx = null;

  const notifyMotionReady = () => {
    ScrollTrigger.refresh();
    window.dispatchEvent(new CustomEvent('kb:motion-ready'));
  };

  const wireScrollMotion = (els, playFirstPaint = true) => {
    scrollCtx?.revert();
    scrollCtx = gsap.context(() => {
      if (playFirstPaint) buildFirstPaint(gsap.timeline());

      for (const el of els) {
        if (isIntroReveal(el)) continue;
        if (el.closest('.faqs')) continue;
        bindReveal(el);
      }
      bindFooter();
      bindMediaParallax();
      notifyMotionReady();
    });
  };

  const runIntro = () => {
    const intro = document.querySelector('[data-motion-intro]');
    const mark = intro?.querySelector('.motion-intro__mark');
    const designMode = isDesignMode();

    if (REDUCED || designMode) {
      intro?.remove();
      html.classList.add('is-intro-done');
      ensureSectionReveals();
      showImmediate();
      return;
    }

    const skip = !intro || html.classList.contains('kb-skip-intro');

    if (skip) {
      intro?.remove();
      html.classList.add('is-intro-done');
      wireScrollMotion(prepare());
      return;
    }

    html.classList.add('is-intro-pending');
    const els = prepare();

    gsap.set(intro, { yPercent: 0, autoAlpha: 1 });
    if (mark) gsap.set(mark, { autoAlpha: 0, y: 20, scale: 0.94 });

    const tl = gsap.timeline({
      onComplete: () => {
        intro.remove();
        html.classList.remove('is-intro-pending');
        html.classList.add('is-intro-done');
        if (REDUCED) {
          showImmediate();
          return;
        }
        wireScrollMotion(els);
      },
    });

    const veilStart = 1;
    if (mark) {
      tl.to(mark, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: isDesktop() ? 0.4 : 0.3,
        ease: EASE_SOFT,
      }, 0);
    }
    tl.to(intro, {
      yPercent: -101,
      duration: isDesktop() ? 1 : 0.75,
      ease: EASE_VEIL,
    }, veilStart);
  };

  desktop.addEventListener('change', () => {
    if (scrollCtx && !REDUCED && !isDesignMode()) {
      const heroContent = document.querySelector('.hero__content-wrapper[data-reveal]');
      const heroKids = heroContent ? targetsFor(heroContent) : [];
      wireScrollMotion(prepare(false), false);
      if (heroKids.length) {
        gsap.set(heroKids, { autoAlpha: 1, y: 0, clearProps: 'transform' });
      }
    } else {
      ScrollTrigger.refresh();
    }
  });

  reducedMotion.addEventListener('change', (event) => {
    REDUCED = event.matches;
    if (!REDUCED) return;
    scrollCtx?.revert();
    scrollCtx = null;
    document.querySelector('[data-motion-intro]')?.remove();
    html.classList.remove('kb-motion-boot', 'is-intro-pending');
    html.classList.add('kb-skip-intro', 'is-intro-done');
    showImmediate();
    notifyMotionReady();
  });

  window.addEventListener('load', notifyMotionReady);

  const refreshScrollMotion = () => {
    if (REDUCED || isDesignMode() || !scrollCtx) return;
    ScrollTrigger.refresh();
  };

  document.addEventListener('visitorConsentCollected', refreshScrollMotion);
  window.addEventListener('load', () => {
    refreshScrollMotion();
    window.setTimeout(refreshScrollMotion, 800);
  });

  const unlockEditor = () => {
    if (!isDesignMode()) return;
    scrollCtx?.revert();
    scrollCtx = null;
    ScrollTrigger.getAll().forEach((t) => t.kill());
    bailForEditor();
    ensureSectionReveals();
    showImmediate();
  };

  ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:unload'].forEach((eventName) => {
    document.addEventListener(eventName, () => requestAnimationFrame(unlockEditor));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runIntro, { once: true });
  } else {
    runIntro();
  }
})();
